<template>
  <div class="dashboard-page-container">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    
    <v-snackbar v-model="showsnackbar" color="#9a6b27" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white; font-weight: 600;">
            {{ msg }}
          </span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showsnackbar = false">
            <v-icon style="color: #ffffff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- 1. GREETING & UPDATE RATES ROW -->
    <section class="dashboard-hero-header mb-4">
      <div class="header-welcome-text">
        <div class="eyebrow-kicker">
          <span class="diamond-bullet">❖</span> GOOD MORNING,
        </div>
        <h1 class="welcome-heading">Welcome back, {{ (userData && (userData.name || userData.username)) || name || 'User' }}!</h1>
        <p class="welcome-subtitle">Manage your jewellery business with precision and confidence.</p>
      </div>

      <div class="header-action-btn-wrapper">
        <button class="update-rates-btn" @click="queryPopUpDialog = true">
          <v-icon left size="18" color="#ffffff">mdi-swap-vertical</v-icon>
          Update Boarding Rates
        </button>
      </div>
    </section>

    <!-- 3. QUICK ACTIONS STRIP -->
    <section class="quick-actions-bar mb-5">
      <div class="quick-actions-badge">
        <v-icon size="16" color="#b9893f" class="mr-1">mdi-flash</v-icon>
        <span>Quick Actions</span>
      </div>
      <div class="quick-actions-grid">
        <router-link to="/EstimatePage" class="quick-action-item">
          <v-icon size="16" color="#8c6227" class="mr-2">mdi-file-document-edit-outline</v-icon>
          <span>New Sales Invoice</span>
        </router-link>
        <router-link to="/PurchaseList" class="quick-action-item">
          <v-icon size="16" color="#3b82f6" class="mr-2">mdi-package-variant-closed</v-icon>
          <span>Add Stock Purchase</span>
        </router-link>
        <router-link to="/StockPage" class="quick-action-item">
          <v-icon size="16" color="#16a34a" class="mr-2">mdi-chart-box-outline</v-icon>
          <span>Stock Valuation</span>
        </router-link>
        <router-link to="/Customer" class="quick-action-item">
          <v-icon size="16" color="#2563eb" class="mr-2">mdi-account-group-outline</v-icon>
          <span>Customer Directory</span>
        </router-link>
      </div>
    </section>

    <!-- 4. 3 KPI METRIC CARDS -->
    <section class="metrics-grid mb-5">
      <!-- Total Sales -->
      <div class="metric-card sales-kpi">
        <div class="metric-card-top">
          <div class="metric-icon-box sales-bg">
            <v-icon size="22" color="#8c6227">mdi-shopping-outline</v-icon>
          </div>
          <v-menu offset-y right>
            <template v-slot:activator="{ on, attrs }">
              <button v-bind="attrs" v-on="on" class="kpi-menu-btn" title="Options">···</button>
            </template>
            <v-list dense class="rounded-lg shadow-sm">
              <v-list-item @click="$router.push('/SalesList')">
                <v-list-item-title>View Sales</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <div class="metric-label">TOTAL SALES</div>
        <div class="metric-value">₹ {{ formatCurrency(totalSales) }}</div>
        <div class="metric-footer">
          <span class="metric-period">Active Period</span>
          <div v-if="salesTrendPct !== null" class="metric-badge" :class="salesTrendPct >= 0 ? 'positive' : 'negative'">
            <v-icon x-small :color="salesTrendPct >= 0 ? '#16a34a' : '#dc2626'">{{ salesTrendPct >= 0 ? 'mdi-arrow-top-right' : 'mdi-arrow-bottom-right' }}</v-icon>
            <span>{{ Math.abs(salesTrendPct) }}%</span>
            <span class="vs-text">vs. last month</span>
          </div>
        </div>
      </div>

      <!-- Total Profit -->
      <div class="metric-card profit-kpi">
        <div class="metric-card-top">
          <div class="metric-icon-box profit-bg">
            <v-icon size="22" color="#16a34a">mdi-trending-up</v-icon>
          </div>
          <v-menu offset-y right>
            <template v-slot:activator="{ on, attrs }">
              <button v-bind="attrs" v-on="on" class="kpi-menu-btn" title="Options">···</button>
            </template>
            <v-list dense class="rounded-lg shadow-sm">
              <v-list-item @click="$router.push('/ProfitAndLoss')">
                <v-list-item-title>View Profit & Loss</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <div class="metric-label">TOTAL PROFIT</div>
        <div class="metric-value">₹ {{ formatCurrency(totalProfit) }}</div>
        <div class="metric-footer">
          <span class="metric-period">Net Profit Margin</span>
          <div v-if="profitTrendPct !== null" class="metric-badge" :class="profitTrendPct >= 0 ? 'positive' : 'negative'">
            <v-icon x-small :color="profitTrendPct >= 0 ? '#16a34a' : '#dc2626'">{{ profitTrendPct >= 0 ? 'mdi-arrow-top-right' : 'mdi-arrow-bottom-right' }}</v-icon>
            <span>{{ Math.abs(profitTrendPct) }}%</span>
            <span class="vs-text">vs. last month</span>
          </div>
        </div>
      </div>

      <!-- Total Purchase -->
      <div class="metric-card purchase-kpi">
        <div class="metric-card-top">
          <div class="metric-icon-box purchase-bg">
            <v-icon size="22" color="#2563eb">mdi-cart-outline</v-icon>
          </div>
          <v-menu offset-y right>
            <template v-slot:activator="{ on, attrs }">
              <button v-bind="attrs" v-on="on" class="kpi-menu-btn" title="Options">···</button>
            </template>
            <v-list dense class="rounded-lg shadow-sm">
              <v-list-item @click="$router.push('/PurchaseList')">
                <v-list-item-title>View Purchases</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <div class="metric-label">TOTAL PURCHASE</div>
        <div class="metric-value">₹ {{ formatCurrency(totalPurchase) }}</div>
        <div class="metric-footer">
          <span class="metric-period">Acquisition Cost</span>
          <div v-if="purchaseTrendPct !== null" class="metric-badge" :class="purchaseTrendPct >= 0 ? 'negative' : 'positive'">
            <v-icon x-small :color="purchaseTrendPct >= 0 ? '#dc2626' : '#16a34a'">{{ purchaseTrendPct >= 0 ? 'mdi-arrow-top-right' : 'mdi-arrow-bottom-right' }}</v-icon>
            <span>{{ Math.abs(purchaseTrendPct) }}%</span>
            <span class="vs-text">vs. last month</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 5. LOWER 2-COLUMN SECTION: CHART & LIVE RATE TRACKER -->
    <section class="dashboard-lower-grid">
      <!-- Left: Sales & Purchase Trend Chart -->
      <div class="panel-card chart-panel">
        <div class="panel-header d-flex flex-wrap align-center justify-space-between mb-2">
          <div class="d-flex align-center">
            <v-icon color="#b9893f" size="20" class="mr-2">mdi-chart-line</v-icon>
            <h3 class="panel-title">Sales & Purchase Trend</h3>
          </div>
          <div class="d-flex align-center flex-wrap">
            <div class="chart-period-select-wrapper mr-4">
              <v-icon x-small color="#64748b" class="mr-1">mdi-calendar-blank-outline</v-icon>
              <select v-model="selectedPeriod" class="chart-period-dropdown">
                <option value="This Year">This Year</option>
              </select>
            </div>
            <div class="chart-legend-row d-flex align-center">
              <span class="legend-item mr-3"><span class="legend-dot sales-dot"></span> Sales</span>
              <span class="legend-item"><span class="legend-dot purchases-dot"></span> Purchases</span>
            </div>
          </div>
        </div>
        <div class="chart-wrapper">
          <apexchart type="area" height="260" :options="chartOptions" :series="series"></apexchart>
        </div>
      </div>

      <!-- Right: Live Rate Tracker -->
      <div class="panel-card tracker-panel">
        <div class="panel-header d-flex align-center justify-space-between mb-3">
          <div class="d-flex align-center">
            <v-icon color="#b9893f" size="20" class="mr-2">mdi-finance</v-icon>
            <h3 class="panel-title">Live Rate Tracker</h3>
          </div>
          <span class="tracker-subtitle">Real-time market rates</span>
        </div>

        <div class="tracker-list">
          <!-- Gold 24K -->
          <div class="tracker-row">
            <div class="tracker-metal-info d-flex align-center">
              <div class="tracker-icon-circle gold-circle mr-2">
                <v-img src="../../assets/icons/badge_gold.png" height="22" width="22" contain></v-img>
              </div>
              <span class="tracker-metal-name">Gold (24K)</span>
            </div>
            <div class="tracker-price-col">
              <span class="tracker-price">₹ {{ formatCurrency(goldRate) }}</span>
              <span class="tracker-unit">/g</span>
            </div>
            <div class="tracker-trend-badge" :class="trendClass(rateTrend && rateTrend.goldRate)">
              <v-icon v-if="rateTrend && rateTrend.goldRate !== null" x-small :color="rateTrend.goldRate >= 0 ? '#16a34a' : '#dc2626'">{{ rateTrend.goldRate >= 0 ? 'mdi-arrow-top-right' : 'mdi-arrow-bottom-right' }}</v-icon>
              <span>{{ trendLabel(rateTrend && rateTrend.goldRate) }}</span>
            </div>
          </div>

          <!-- Silver -->
          <div class="tracker-row">
            <div class="tracker-metal-info d-flex align-center">
              <div class="tracker-icon-circle silver-circle mr-2">
                <v-img src="../../assets/icons/badge_silver.png" height="22" width="22" contain></v-img>
              </div>
              <span class="tracker-metal-name">Silver</span>
            </div>
            <div class="tracker-price-col">
              <span class="tracker-price">₹ {{ formatCurrency(silverRate) }}</span>
              <span class="tracker-unit">/g</span>
            </div>
            <div class="tracker-trend-badge" :class="trendClass(rateTrend && rateTrend.silverRate)">
              <v-icon v-if="rateTrend && rateTrend.silverRate !== null" x-small :color="rateTrend.silverRate >= 0 ? '#16a34a' : '#dc2626'">{{ rateTrend.silverRate >= 0 ? 'mdi-arrow-top-right' : 'mdi-arrow-bottom-right' }}</v-icon>
              <span>{{ trendLabel(rateTrend && rateTrend.silverRate) }}</span>
            </div>
          </div>

          <!-- Thankam -->
          <div class="tracker-row">
            <div class="tracker-metal-info d-flex align-center">
              <div class="tracker-icon-circle thankam-circle mr-2">
                <v-img src="../../assets/icons/badge_thankam.png" height="22" width="22" contain></v-img>
              </div>
              <span class="tracker-metal-name">Thankam</span>
            </div>
            <div class="tracker-price-col">
              <span class="tracker-price">₹ {{ formatCurrency(thankamRate) }}</span>
              <span class="tracker-unit">/g</span>
            </div>
            <div class="tracker-trend-badge" :class="trendClass(rateTrend && rateTrend.thankamRate)">
              <v-icon v-if="rateTrend && rateTrend.thankamRate !== null" x-small :color="rateTrend.thankamRate >= 0 ? '#16a34a' : '#dc2626'">{{ rateTrend.thankamRate >= 0 ? 'mdi-arrow-top-right' : 'mdi-arrow-bottom-right' }}</v-icon>
              <span>{{ trendLabel(rateTrend && rateTrend.thankamRate) }}</span>
            </div>
          </div>

          <!-- Diamond -->
          <div class="tracker-row">
            <div class="tracker-metal-info d-flex align-center">
              <div class="tracker-icon-circle diamond-circle mr-2">
                <v-img src="../../assets/icons/badge_diamond.png" height="22" width="22" contain></v-img>
              </div>
              <span class="tracker-metal-name">Diamond</span>
            </div>
            <div class="tracker-price-col">
              <span class="tracker-price">₹ {{ formatCurrency(diamond) }}</span>
            </div>
            <div class="tracker-trend-badge" :class="trendClass(rateTrend && rateTrend.diamond)">
              <v-icon v-if="rateTrend && rateTrend.diamond !== null" x-small :color="rateTrend.diamond >= 0 ? '#16a34a' : '#dc2626'">{{ rateTrend.diamond >= 0 ? 'mdi-arrow-top-right' : 'mdi-arrow-bottom-right' }}</v-icon>
              <span>{{ trendLabel(rateTrend && rateTrend.diamond) }}</span>
            </div>
          </div>

          <!-- Platinum -->
          <div class="tracker-row">
            <div class="tracker-metal-info d-flex align-center">
              <div class="tracker-icon-circle platinum-circle mr-2">
                <v-img src="../../assets/icons/badge_platinum.png" height="22" width="22" contain></v-img>
              </div>
              <span class="tracker-metal-name">Platinum</span>
            </div>
            <div class="tracker-price-col">
              <span class="tracker-price">₹ {{ formatCurrency(platinumRate) }}</span>
              <span class="tracker-unit">/g</span>
            </div>
            <div class="tracker-trend-badge" :class="trendClass(rateTrend && rateTrend.platinumRate)">
              <v-icon v-if="rateTrend && rateTrend.platinumRate !== null" x-small :color="rateTrend.platinumRate >= 0 ? '#16a34a' : '#dc2626'">{{ rateTrend.platinumRate >= 0 ? 'mdi-arrow-top-right' : 'mdi-arrow-bottom-right' }}</v-icon>
              <span>{{ trendLabel(rateTrend && rateTrend.platinumRate) }}</span>
            </div>
          </div>

          <!-- 18-CT -->
          <div class="tracker-row">
            <div class="tracker-metal-info d-flex align-center">
              <div class="tracker-icon-circle gold-circle mr-2">
                <v-img src="../../assets/icons/badge_18ct.png" height="22" width="22" contain></v-img>
              </div>
              <span class="tracker-metal-name">18-CT</span>
            </div>
            <div class="tracker-price-col">
              <span class="tracker-price">₹ {{ formatCurrency(goldRate ? Math.round(goldRate * 18 / 22) : 0) }}</span>
              <span class="tracker-unit">/g</span>
            </div>
            <div class="tracker-trend-badge" :class="trendClass(rateTrend && rateTrend.goldRate)">
              <v-icon v-if="rateTrend && rateTrend.goldRate !== null" x-small :color="rateTrend.goldRate >= 0 ? '#16a34a' : '#dc2626'">{{ rateTrend.goldRate >= 0 ? 'mdi-arrow-top-right' : 'mdi-arrow-bottom-right' }}</v-icon>
              <span>{{ trendLabel(rateTrend && rateTrend.goldRate) }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Dialog for boarding rate editing -->
    <v-dialog
      v-model="queryPopUpDialog"
      persistent
      max-width="600px"
    >
      <v-card class="boarding-dialog-card pa-6">
        <div class="dialog-header mb-5 d-flex align-center justify-space-between">
          <div>
            <h2 class="dialog-title">
              <v-icon color="#9a6b27" class="mr-2">mdi-tune-vertical-variant</v-icon>
              Update Boarding Rates
            </h2>
            <p class="dialog-subtitle">Set current market prices per gram for metals and commodities</p>
          </div>
          <v-btn icon small @click="queryPopUpDialog = false">
            <v-icon small color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-layout wrap>
          <!-- Thankam -->
          <v-flex xs12 sm6 pa-2>
            <div class="dialog-input-group">
              <label class="dialog-input-label">THANKAM (Per Gram)</label>
              <v-text-field
                v-model="thankamRate"
                prefix="₹"
                type="number"
                outlined
                dense
                placeholder="0.00"
                class="dialog-text-field"
                hide-details
              ></v-text-field>
            </div>
          </v-flex>

          <!-- Gold -->
          <v-flex xs12 sm6 pa-2>
            <div class="dialog-input-group">
              <label class="dialog-input-label">GOLD (Per Gram)</label>
              <v-text-field
                v-model="gold"
                prefix="₹"
                type="number"
                outlined
                dense
                placeholder="0.00"
                class="dialog-text-field"
                hide-details
              ></v-text-field>
            </div>
          </v-flex>

          <!-- Silver -->
          <v-flex xs12 sm6 pa-2>
            <div class="dialog-input-group">
              <label class="dialog-input-label">SILVER (Per Gram)</label>
              <v-text-field
                v-model="silverRate"
                prefix="₹"
                type="number"
                outlined
                dense
                placeholder="0.00"
                class="dialog-text-field"
                hide-details
              ></v-text-field>
            </div>
          </v-flex>

          <!-- Platinum -->
          <v-flex xs12 sm6 pa-2>
            <div class="dialog-input-group">
              <label class="dialog-input-label">PLATINUM (Per Gram)</label>
              <v-text-field
                v-model="platinumRate"
                prefix="₹"
                type="number"
                outlined
                dense
                placeholder="0.00"
                class="dialog-text-field"
                hide-details
              ></v-text-field>
            </div>
          </v-flex>

          <!-- Diamond -->
          <v-flex xs12 sm6 pa-2>
            <div class="dialog-input-group">
              <label class="dialog-input-label">DIAMOND Rate</label>
              <v-text-field
                v-model="diamond"
                prefix="₹"
                type="number"
                outlined
                dense
                placeholder="0.00"
                class="dialog-text-field"
                hide-details
              ></v-text-field>
            </div>
          </v-flex>
        </v-layout>

        <div class="dialog-actions-row mt-6">
          <v-btn
            outlined
            color="#8d7c61"
            height="40px"
            class="mr-3 text-capitalize font-weight-bold rounded-lg"
            @click="queryPopUpDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="#9a6b27"
            dark
            depressed
            height="40px"
            class="text-capitalize font-weight-bold rounded-lg"
            @click="EditRate()"
          >
            Save Rates
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import store from "./../../store";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2000,
      msg: null,
      gold: "",
      goldRate: "",
      thankamRate: "",
      silverRate: "",
      platinumRate: "",
      diamond: "",
      date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      queryPopUpDialog: false,
      username: null,
      name: null,
      address: null,
      phone: null,
      totalSales: 0,
      totalProfit: 0,
      totalPurchase: 0,
      salesTrendPct: null,
      purchaseTrendPct: null,
      profitTrendPct: null,
      rateTrend: null,
      selectedPeriod: "This Year",

      series: [
        { name: "Sales", data: new Array(12).fill(0) },
        { name: "Purchases", data: new Array(12).fill(0) },
      ],
      chartOptions: {
        colors: ["#b9893f", "#6b7280"],
        chart: {
          height: 260,
          type: "area",
          fontFamily: "'Inter', sans-serif",
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        dataLabels: { enabled: false },
        stroke: {
          curve: "smooth",
          width: [2.5, 2],
        },
        markers: {
          size: [4, 3],
          colors: ["#b9893f", "#6b7280"],
          strokeColors: "#ffffff",
          strokeWidth: 2,
          hover: { size: 6 }
        },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.3,
            opacityTo: 0.02,
            stops: [0, 95, 100]
          }
        },
        xaxis: {
          categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            style: {
              colors: "#94a3b8",
              fontSize: "11px",
              fontFamily: "'Inter', sans-serif"
            }
          }
        },
        yaxis: {
          min: 0,
          tickAmount: 3,
          labels: {
            style: {
              colors: "#94a3b8",
              fontSize: "11px",
              fontFamily: "'Inter', sans-serif"
            },
            formatter: (val) => {
              if (val === 0) return "₹ 0";
              return "₹ " + Number(val).toLocaleString("en-IN");
            }
          }
        },
        grid: {
          borderColor: "#f0ece6",
          strokeDashArray: 4,
          yaxis: { lines: { show: true } },
          xaxis: { lines: { show: false } },
          padding: { top: 0, right: 10, bottom: 0, left: 10 }
        },
        legend: { show: false },
        tooltip: {
          theme: "light",
          y: {
            formatter: (val) => "₹ " + Number(val).toLocaleString("en-IN")
          }
        }
      },
    };
  },
  mounted() {
    this.userinfo();
    this.getBording();
    this.getDashboardStats();
    this.$root.$on('open-boarding-rates-dialog', () => {
      this.queryPopUpDialog = true;
    });
  },
  beforeDestroy() {
    this.$root.$off('open-boarding-rates-dialog');
  },
  computed: {
    userData() {
      return this.$store.state.userData;
    },
    chartSeries() {
      return this.series;
    }
  },
  methods: {
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val) || val === "") return '0.00';
      return Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    trendClass(pct) {
      if (pct === null || pct === undefined) return 'neutral';
      return pct >= 0 ? 'positive' : 'negative';
    },
    trendLabel(pct) {
      if (pct === null || pct === undefined) return '—';
      return Math.abs(pct) + '%';
    },
    getDashboardStats() {
      axios({
        url: "/dashboard/summary",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data.status && response.data.data) {
            const data = response.data.data;
            this.totalSales = data.totalSales || 0;
            this.totalPurchase = data.totalPurchase || 0;
            this.totalProfit = data.totalProfit || 0;
            this.salesTrendPct = data.salesTrendPct ?? null;
            this.purchaseTrendPct = data.purchaseTrendPct ?? null;
            this.profitTrendPct = data.profitTrendPct ?? null;
            this.rateTrend = data.rateTrend || null;
            if (data.salesMonthly && data.purchaseMonthly) {
              this.series = [
                { name: "Sales", data: data.salesMonthly },
                { name: "Purchases", data: data.purchaseMonthly }
              ];
            }
          }
        })
        .catch((err) => {
          console.log("Error fetching dashboard stats:", err);
        });
    },
    userinfo() {
      axios({
        method: "GET",
        url: "/user/info",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: localStorage.getItem("id"),
        },
      })
        .then((response) => {
          if (response.data.status) {
            this.name = response.data.data.name || response.data.data.username || response.data.data.fullName;
            localStorage.setItem("GSTstatus", response.data.data.taxType);
          } else {
            store.commit("sessionOut", true);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getBording() {
      this.appLoading = true;
      axios({
        url: "/rate/view",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          date: this.billDate,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.diamond = response.data.data.diamond;
            this.goldRate = response.data.data.goldRate;
            this.gold = response.data.data.goldRate;
            this.platinumRate = response.data.data.platinumRate;
            this.silverRate = response.data.data.silverRate;
            this.thankamRate = response.data.data.thankamRate;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
    EditRate() {
      axios({
        url: "/rate/add",
        method: "POST",
        data: {
          goldRate: this.gold,
          silverRate: this.silverRate,
          platinumRate: this.platinumRate,
          diamond: this.diamond,
          date: this.date,
          thankamRate: this.thankamRate,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.queryPopUpDialog = false;
            this.getBording();
            location.reload();
            this.date = null;
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@500;600;700&family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.dashboard-page-container {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  padding: 20px 24px;
  background-color: transparent;
}

/* 1. Hero Header */
.dashboard-hero-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  text-align: left;
}

.header-welcome-text {
  text-align: left;
}

.eyebrow-kicker {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #9a6b27;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 4px;
}

.diamond-bullet {
  font-size: 13px;
  margin-right: 5px;
  color: #9a6b27;
}

.welcome-heading {
  font-family: 'Cinzel', 'Inter', serif;
  font-size: 28px;
  font-weight: 700;
  color: #172238;
  letter-spacing: -0.2px;
  line-height: 1.2;
  margin: 0;
  text-align: left;
}

.welcome-subtitle {
  font-size: 13.5px;
  color: #64748b;
  margin: 4px 0 0 0;
}

.update-rates-btn {
  background: #9a6a2c !important;
  color: #ffffff !important;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  padding: 10px 18px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(154, 106, 44, 0.25);
  transition: all 0.2s ease;
}

.update-rates-btn:hover {
  background: #875c24 !important;
  box-shadow: 0 4px 12px rgba(154, 106, 44, 0.35);
  transform: translateY(-1px);
}

/* 3. Quick Actions */
.quick-actions-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  background: #ffffff;
  border: 1px solid #eeebe4;
  border-radius: 12px;
  padding: 10px 18px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.02);
}

.quick-actions-badge {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  color: #172238;
}

.quick-actions-grid {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  flex: 1;
}

.quick-action-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background: #fbfaf8;
  border: 1px solid #ebe5db;
  border-radius: 10px;
  text-decoration: none;
  color: #172238;
  font-size: 12.5px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.quick-action-item:hover {
  background: #ffffff;
  border-color: #b9893f;
  color: #9a6b27;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

/* 4. KPI Metrics Cards */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

@media (max-width: 960px) {
  .metrics-grid {
    grid-template-columns: 1fr;
  }
}

.metric-card {
  background: #ffffff;
  border: 1px solid #eeebe4;
  border-radius: 14px;
  padding: 18px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
}

.metric-card:hover {
  border-color: #dfd7cc;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  transform: translateY(-1px);
}

.metric-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.metric-icon-box {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sales-bg {
  background: #fbf5eb;
  border: 1px solid #f2e6d4;
}

.profit-bg {
  background: #edfbf2;
  border: 1px solid #d9f3e1;
}

.purchase-bg {
  background: #eef4ff;
  border: 1px solid #dbe6fe;
}

.kpi-menu-btn {
  background: transparent;
  border: none;
  font-size: 18px;
  font-weight: 700;
  color: #94a3b8;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.kpi-menu-btn:hover {
  color: #172238;
}

.metric-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #64748b;
  margin-bottom: 4px;
}

.metric-value {
  font-family: 'Inter', sans-serif;
  font-size: 26px;
  font-weight: 700;
  color: #172238;
  letter-spacing: -0.5px;
  margin-bottom: 10px;
}

.metric-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #f6f3ee;
  padding-top: 10px;
}

.metric-period {
  font-size: 12px;
  color: #8d8a98;
  font-weight: 500;
}

.metric-badge {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 700;
}

.metric-badge.positive {
  color: #16a34a;
}

.metric-badge.negative {
  color: #dc2626;
}

.vs-text {
  font-size: 10px;
  color: #94a3b8;
  font-weight: 400;
  margin-left: 2px;
}

/* 5. Lower Section (2 Columns) */
.dashboard-lower-grid {
  display: grid;
  grid-template-columns: 1.85fr 1fr;
  gap: 16px;
}

@media (max-width: 1100px) {
  .dashboard-lower-grid {
    grid-template-columns: 1fr;
  }
}

.panel-card {
  background: #ffffff;
  border: 1px solid #eeebe4;
  border-radius: 14px;
  padding: 20px 22px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.panel-title {
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #172238;
  margin: 0;
}

.chart-period-select-wrapper {
  display: flex;
  align-items: center;
  background: #fbfaf8;
  border: 1px solid #e7e2da;
  border-radius: 8px;
  padding: 4px 8px;
}

.chart-period-dropdown {
  border: none;
  background: transparent;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  outline: none;
  cursor: pointer;
}

.chart-legend-row {
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.sales-dot {
  background: #b9893f;
}

.purchases-dot {
  background: #6b7280;
}

.chart-wrapper {
  margin-top: 10px;
}

.tracker-subtitle {
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
}

.tracker-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tracker-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #fbfaf8;
  border: 1px solid #f0ebe4;
  border-radius: 10px;
  transition: all 0.2s;
}

.tracker-row:hover {
  background: #ffffff;
  border-color: #dfd7cc;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.02);
}

.tracker-icon-circle {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.gold-circle {
  background: #fef8e7;
}

.silver-circle {
  background: #f1f3f6;
}

.thankam-circle {
  background: #fef8e7;
}

.diamond-circle {
  background: #edf4ff;
}

.platinum-circle {
  background: #f1f3f6;
}

.tracker-metal-name {
  font-size: 12.5px;
  font-weight: 600;
  color: #172238;
}

.tracker-price-col {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.tracker-price {
  font-size: 13px;
  font-weight: 700;
  color: #172238;
}

.tracker-unit {
  font-size: 10px;
  color: #8d8a98;
}

.tracker-trend-badge {
  display: flex;
  align-items: center;
  font-size: 10.5px;
  font-weight: 600;
  min-width: 48px;
  justify-content: flex-end;
}

.tracker-trend-badge.positive {
  color: #16a34a;
}

.tracker-trend-badge.negative {
  color: #dc2626;
}

.tracker-trend-badge.neutral {
  color: #94a3b8;
}

/* Boarding Rate Dialog */
.boarding-dialog-card {
  border-radius: 16px !important;
  background: #ffffff !important;
  border: 1px solid #eeebe4;
}

.dialog-title {
  font-family: 'Cinzel', serif;
  font-size: 20px;
  font-weight: 700;
  color: #172238;
  display: flex;
  align-items: center;
}

.dialog-subtitle {
  font-size: 12.5px;
  color: #64748b;
  margin-top: 2px;
}

.dialog-input-group {
  display: flex;
  flex-direction: column;
}

.dialog-input-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.dialog-text-field >>> .v-input__slot {
  border-radius: 8px !important;
  border: 1px solid #e2ddf0 !important;
}

.dialog-actions-row {
  display: flex;
  justify-content: flex-end;
}
</style>
